import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { brl, fmtData, fmtMes, mesAtual, mesAnterior, proximoMes } from '../lib/format';
import { DashboardData, Tarefa } from '../types';

export function Dashboard() {
  const [mes, setMes] = useState(mesAtual());
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const carregar = () => {
    setLoading(true);
    api.get<DashboardData>(`/dashboard?mes=${mes}`).then(setData).finally(() => setLoading(false));
  };

  useEffect(carregar, [mes]);

  const gerarRecebimentos = async () => {
    if (!confirm(`Gerar recebimentos pendentes para ${fmtMes(mes)} em todos os imóveis ativos?`)) return;
    const r = await api.post<{ criados: number }>('/recebimentos/gerar', { mes });
    alert(`${r.criados} recebimento(s) criado(s).`);
    carregar();
  };

  const marcarPago = async (rec: any) => {
    const valor = prompt('Valor recebido:', String(rec.valor_previsto));
    if (!valor) return;
    await api.post('/recebimentos', {
      imovel_id: rec.imovel_id,
      mes_referencia: rec.mes_referencia,
      valor_previsto: rec.valor_previsto,
      valor_recebido: Number(valor),
      data_pagamento: new Date().toISOString().slice(0, 10)
    });
    carregar();
  };

  const concluirTarefa = async (t: Tarefa) => {
    await api.put(`/tarefas/${t.id}`, { ...t, feito: 1 });
    carregar();
  };

  if (loading || !data) return <div className="text-slate-500">Carregando...</div>;

  const t = data.totais;
  const donos = Object.entries(data.porDono);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold hidden sm:block">Dashboard</h1>
        <div className="flex items-center gap-2">
          <button className="btn-ghost px-2" onClick={() => setMes(mesAnterior(mes))}>‹</button>
          <input
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="input w-40"
          />
          <button className="btn-ghost px-2" onClick={() => setMes(proximoMes(mes))}>›</button>
        </div>
      </div>

      {/* Alertas */}
      {data.alertas.length > 0 && (
        <div className="card p-4 border-l-4 border-l-yellow-500">
          <div className="font-semibold text-slate-900 mb-2">Atenção · {data.alertas.length}</div>
          <ul className="text-sm space-y-1">
            {data.alertas.map((a, i) => (
              <li key={i} className="flex justify-between">
                <span>
                  <span className={a.tipo === 'atrasado' ? 'badge-red mr-2' : 'badge-yellow mr-2'}>
                    {a.tipo === 'atrasado' ? 'atrasado' : 'próximo'}
                  </span>
                  {a.recebimento.imovel_apelido} (vence dia {a.recebimento.dia_vencimento})
                </span>
                <span className="font-medium">{brl(a.recebimento.valor_previsto)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Totais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <CardTotal titulo="A receber" valor={t.previsto} />
        <CardTotal titulo="Recebido" valor={t.recebido} cor="green" />
        <CardTotal titulo="Pendente" valor={t.pendente} cor="yellow" />
        <CardTotal titulo="Despesas" valor={t.despesas} cor="red" />
      </div>

      <div className="card-pad">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-slate-500">Resultado líquido do mês</span>
          <span className={`text-2xl font-bold ${t.liquido >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {brl(t.liquido)}
          </span>
        </div>
      </div>

      {/* Tarefas pendentes */}
      {data.tarefas.length > 0 && (
        <div className="card-pad">
          <h2 className="font-semibold mb-3">Tarefas pendentes · {data.tarefas.length}</h2>
          <ul className="divide-y divide-slate-100">
            {data.tarefas.map((t) => {
              const atrasada = t.prazo && new Date(t.prazo) < new Date(new Date().toDateString());
              return (
                <li key={t.id} className="py-2 flex items-start gap-3">
                  <input
                    type="checkbox"
                    onChange={() => concluirTarefa(t)}
                    className="mt-1 h-4 w-4 cursor-pointer"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{t.titulo}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                      {t.imovel_apelido && (
                        <Link to={`/imoveis/${t.imovel_id}`} className="text-brand-600 hover:underline">
                          {t.imovel_apelido}
                        </Link>
                      )}
                      <span className={
                        t.prioridade === 'alta' ? 'badge-red'
                          : t.prioridade === 'media' ? 'badge-yellow'
                          : 'badge-slate'
                      }>{t.prioridade}</span>
                      {t.prazo && (
                        <span className={atrasada ? 'text-red-600 font-medium' : ''}>
                          prazo {fmtData(t.prazo)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Promissórias atrasadas + a vencer (30d) */}
      {data.promissorias.length > 0 && (
        <div className="card-pad border-l-4 border-l-red-500">
          <h2 className="font-semibold mb-2">Promissórias · {data.promissorias.length}</h2>
          <ul className="divide-y divide-slate-100 text-sm">
            {data.promissorias.map((p) => (
              <li key={p.id} className="py-2 flex justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/imoveis/${p.imovel_id}`} className="font-medium hover:text-brand-600 truncate block">
                    {p.imovel_apelido} · {p.descricao}
                    {p.numero && p.total && <span className="text-xs text-slate-500"> ({p.numero}/{p.total})</span>}
                  </Link>
                  <div className="text-xs">
                    <span className={p.atrasada ? 'badge-red mr-2' : 'badge-yellow mr-2'}>
                      {p.atrasada ? 'atrasada' : 'a vencer'}
                    </span>
                    {fmtData(p.vencimento)}
                    {p.valor_pago > 0 && ` · pago ${brl(p.valor_pago)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold">{brl(p.valor - p.valor_pago)}</div>
                  <div className="text-xs text-slate-500">em aberto</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Imóveis desocupados */}
      {data.desocupados.length > 0 && (
        <div className="card-pad border-l-4 border-l-yellow-500">
          <h2 className="font-semibold mb-2">Desocupados · {data.desocupados.length}</h2>
          <ul className="space-y-1 text-sm">
            {data.desocupados.map((im) => {
              const dias = im.desocupado_desde
                ? Math.floor((Date.now() - new Date(im.desocupado_desde).getTime()) / (1000 * 60 * 60 * 24))
                : null;
              return (
                <li key={im.id} className="flex justify-between">
                  <Link to={`/imoveis/${im.id}`} className="font-medium hover:text-brand-600 truncate">
                    {im.apelido}
                  </Link>
                  <span className="text-slate-500">
                    {dias != null ? `${dias} dia${dias !== 1 ? 's' : ''}` : '—'}
                    {' · '}
                    {brl(im.valor_aluguel)}/mês perdido
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Por dono */}
      {donos.length > 1 && (
        <div className="card-pad">
          <h2 className="font-semibold mb-3">Por dono</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {donos.map(([dono, v]) => (
              <div key={dono} className="border border-slate-200 rounded-lg p-3">
                <div className="font-medium">{dono}</div>
                <div className="text-xs text-slate-500 mt-1">Recebido</div>
                <div className="text-lg font-semibold text-green-700">{brl(v.recebido)}</div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="text-slate-500">Previsto:</span> {brl(v.previsto)}</div>
                  <div><span className="text-slate-500">Despesas:</span> {brl(v.despesas)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recebimentos */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Recebimentos · {fmtMes(mes)}</h2>
          <button className="btn-secondary text-xs" onClick={gerarRecebimentos}>+ Gerar mês</button>
        </div>
        {data.recebimentos.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum recebimento. Clique em "Gerar mês" para criar a partir dos imóveis ativos.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recebimentos.map((r) => {
              const pago = r.valor_recebido != null;
              return (
                <li key={r.id} className="py-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/imoveis/${r.imovel_id}`} className="font-medium text-slate-900 hover:text-brand-600 block truncate">
                      {r.imovel_apelido}
                    </Link>
                    <div className="text-xs text-slate-500 truncate">
                      {r.imovel_dono} · vence dia {r.dia_vencimento}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-semibold ${pago ? 'text-green-700' : 'text-slate-900'}`}>
                      {brl(pago ? r.valor_recebido : r.valor_previsto)}
                    </div>
                    {pago ? (
                      <span className="badge-green">pago</span>
                    ) : (
                      <button className="text-xs text-brand-600 hover:underline" onClick={() => marcarPago(r)}>
                        marcar pago
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Despesas resumo */}
      <div className="card-pad">
        <h2 className="font-semibold mb-3">Despesas · {fmtMes(mes)}</h2>
        {data.despesas.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma despesa registrada.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.despesas.map((d) => (
              <li key={d.id} className="py-2 flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate">{d.descricao}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {d.imovel_apelido ?? 'Geral'} · {d.categoria}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={d.pago ? 'text-slate-600' : 'text-red-700 font-semibold'}>{brl(d.valor)}</div>
                  <span className={d.pago ? 'badge-green' : 'badge-yellow'}>{d.pago ? 'pago' : 'pendente'}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CardTotal({ titulo, valor, cor }: { titulo: string; valor: number; cor?: 'green' | 'yellow' | 'red' }) {
  const map = {
    green: 'text-green-700',
    yellow: 'text-yellow-700',
    red: 'text-red-700'
  } as const;
  return (
    <div className="card-pad">
      <div className="text-xs text-slate-500">{titulo}</div>
      <div className={`text-lg sm:text-xl font-bold mt-1 ${cor ? map[cor] : 'text-slate-900'}`}>{brl(valor)}</div>
    </div>
  );
}
